import { useCallback, useRef } from "react";
import { useViewStateStore } from "@/stores/useViewStateStore";

type StateSetter<T> = T | ((current: T) => T);

/** Route-lifetime UI state. Deliberately cleared by page reload and logout. */
export const useNavigationState = <T,>(key: string, initialValue: T) => {
  const initialValueRef = useRef(initialValue);
  const storedValue = useViewStateStore((state) => state.values[key]);
  const setValue = useViewStateStore((state) => state.setValue);
  const value = (storedValue === undefined ? initialValueRef.current : storedValue) as T;

  const setState = useCallback(
    (nextValue: StateSetter<T>) => {
      const current = (useViewStateStore.getState().values[key] ?? initialValueRef.current) as T;
      setValue(key, typeof nextValue === "function" ? (nextValue as (current: T) => T)(current) : nextValue);
    },
    [key, setValue],
  );

  return [value, setState] as const;
};
